import React, { memo, useState, useEffect, useRef } from "react";
import { FaChevronDown, FaChevronRight } from "react-icons/fa";
import { MdSubdirectoryArrowRight } from "react-icons/md";
import { QuestionTypes } from "js/Constants/forms";
import toast from "react-hot-toast";
import Loader from "js/Component/Loader";
import NoDataFound from "js/Component/NoDataFound";
import DeleteModal from "js/Component/DeleteModal";
import AddEditQuestionModal from "./AddEditQuestionModal";
import PreviewQuestionModal from "./PreviewQuestionModal";
import AddEditSubQuestionModal from "./AddEditSubQuestionModal";
import {
    deleteFormField,
    fetchAllFormFields,
    updateFormFieldOrder,
    duplicateFormField,
    getcustom_paramter_data,
} from "js/Api/forms";

const QuestionsManagement = (props) => {
    const { formDetailsObj, org, onFormUpdate, isLoading: parentLoading } = props;

    const [questionList, setQuestionList] = useState([]);
    const [subQuestionList, setSubQuestionList] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isDuplicating, setIsDuplicating] = useState(false);
    const [customEmailText, setcustomEmailText] = useState("");
    const [expandedQuestions, setExpandedQuestions] = useState(new Set());
    const [expandedOptions, setExpandedOptions] = useState(new Set());

    const [draggedItem, setDraggedItem] = useState(null);
    const [dragOverIndex, setDragOverIndex] = useState(null);

    const [isEditMode, setIsEditMode] = useState(false);
    const [tempQuestionList, setTempQuestionList] = useState([]);

    const [isPreviewQuestionModalVisible, setisPreviewQuestionModalVisible] = useState(false);
    const [PreviewAbleQuestionObj, setPreviewAbleQuestionObj] = useState(null);

    const [isAddEditQuestionModalVisible, setIsAddEditQuestionModalVisible] = useState(false);
    const [editAbleQuestionObj, setEditAbleQuestionObj] = useState(null);
    const [isDeleteFormModal, setIsDeleteFormModal] = useState(false);
    const [
        isAddEditSubQuestionModalVisible,
        setIsAddEditSubQuestionModalVisible,
    ] = useState(false);
    const [selectedSubQuestionForEdit, setSelectedSubQuestionForEdit] =
        useState(null);

    const selectedQuestionForDelete = useRef(null);
    const formType = formDetailsObj?.formType || "general";

    const sortArrayBasedOnFieldOrder = (list) => {
        return list?.sort?.((a, b) => a.fieldOrder - b.fieldOrder) || [];
    };

    const sortArrayBasedOnOptionOrder = (list) => {
        return list?.sort?.((a, b) => a.optionOrder - b.optionOrder) || [];
    };

    const fetchAllQuestionListHandler = async () => {
        if (!formDetailsObj?.id) return;

        setIsLoading(true);
        try {
            const response = await fetchAllFormFields(formDetailsObj.id);
            if (response?.success && response?.data) {
                const mainQuestions = sortArrayBasedOnFieldOrder(
                    response.data.filter((a) => a.refFieldOption === null)
                );
                const subQuestions = sortArrayBasedOnFieldOrder(
                    response.data.filter((a) => a.refFieldOption !== null)
                );
                setQuestionList(mainQuestions);
                setSubQuestionList(subQuestions);
            }

            const email_resp = await getcustom_paramter_data("email_action");
            if (email_resp?.success && email_resp?.data) {
                setcustomEmailText(email_resp?.data.email);
            }
        } catch (error) {
            console.error("Error fetching questions:", error);
            toast.error("Failed to fetch questions");
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchAllQuestionListHandler();
    }, [formDetailsObj?.id]);

    const toggleQuestionExpand = (questionId) => {
        setExpandedQuestions((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(questionId)) {
                newSet.delete(questionId);
            } else {
                newSet.add(questionId);
            }
            return newSet;
        });
    };

    const toggleOptionExpand = (optionId) => {
        setExpandedOptions((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(optionId)) {
                newSet.delete(optionId);
            } else {
                newSet.add(optionId);
            }
            return newSet;
        });
    };

    const editClickHandler = (e, question) => {
        e.stopPropagation();
        if (question.refFieldOption) {
            setSelectedSubQuestionForEdit(question);
            setIsAddEditSubQuestionModalVisible(true);
        } else {
            setEditAbleQuestionObj(question);
            setIsAddEditQuestionModalVisible(true);
        }
    };

    const copyClickHandler = async (e, question) => {
        e.stopPropagation();
        if (!question?.id) {
            toast.error("Invalid question ID");
            return;
        }

        setIsDuplicating(true);
        try {
            const response = await duplicateFormField(question.id);
            if (response?.success) {
                toast.success("Question duplicated successfully");
                fetchAllQuestionListHandler();
            } else {
                toast.error(response?.error || "Failed to duplicate question");
            }
        } catch (error) {
            toast.error("Failed to duplicate question");
            console.error("Error duplicating question:", error);
        } finally {
            setIsDuplicating(false);
        }
    };

    const deleteClickHandler = (e, question) => {
        e.stopPropagation();
        selectedQuestionForDelete.current = question;
        setIsDeleteFormModal(true);
    };

    const deletedFromHandler = async () => {
        if (selectedQuestionForDelete.current?.id) {
            try {
                const response = await deleteFormField(
                    selectedQuestionForDelete.current.id
                );
                if (response?.success) {
                    fetchAllQuestionListHandler();
                    toast.success("Question deleted successfully");
                    setIsDeleteFormModal(false);
                } else {
                    toast.error(response?.error ?? "Failed to delete question");
                }
            } catch (error) {
                toast.error("Failed to delete question");
                console.error("Error deleting question:", error);
            }
        }
    };

    const previewQuestionClickHandler = (e, question) => {
        e.stopPropagation();
        setisPreviewQuestionModalVisible(true);
        setPreviewAbleQuestionObj(question);
    };

    const onPreviewQuestionCloseHandler = () => {
        setisPreviewQuestionModalVisible(false);
        setPreviewAbleQuestionObj(null);
    };

    const updateQuestionOrderHandler = async (newList) => {
        const storedUserInfo = localStorage.getItem("userInfo") ?? "{}";
        const parsedUserInfo = JSON.parse(storedUserInfo);
        const payload = newList?.map((obj, index) => ({
            id: obj?.id,
            fieldOrder: index + 1,
            userID: parsedUserInfo?.userId,
        }));

        try {
            const response = await updateFormFieldOrder(payload);
            if (response?.success) {
                fetchAllQuestionListHandler();
                toast.success("Question ordered successfully");
            }
        } catch (error) {
            toast.error("Failed to update question order");
            console.error("Error updating question order:", error);
        }
    };

    const handleDragStart = (e, item, index) => {
        if (!isEditMode) {
            e.preventDefault();
            return;
        }
        setDraggedItem({ item, index });
        e.dataTransfer.effectAllowed = "move";
        e.dataTransfer.setData("text/html", e.target.outerHTML);
        e.target.style.opacity = "0.5";
    };

    const handleDragEnd = (e) => {
        e.target.style.opacity = "1";
        setDraggedItem(null);
        setDragOverIndex(null);
    };

    const handleDragOver = (e, index) => {
        if (!isEditMode) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
        setDragOverIndex(index);
    };

    const handleDragLeave = () => {
        setDragOverIndex(null);
    };

    const handleDrop = (e, dropIndex) => {
        if (!isEditMode) return;
        e.preventDefault();

        if (!draggedItem || draggedItem.index === dropIndex) {
            setDragOverIndex(null);
            return;
        }

        const newQuestionList = [...tempQuestionList];
        const draggedQuestion = newQuestionList[draggedItem.index];


        newQuestionList.splice(draggedItem.index, 1);

        newQuestionList.splice(dropIndex, 0, draggedQuestion);

        setTempQuestionList(newQuestionList);
        setDragOverIndex(null);
    };

    const handleEditOrderClick = () => {
        setIsEditMode(true);
        setTempQuestionList([...questionList]);
    };

    const handleCancelEdit = () => {
        setIsEditMode(false);
        setTempQuestionList([]);
        setDraggedItem(null);
        setDragOverIndex(null);
    };

    const handleSaveOrder = async () => {
        if (tempQuestionList.length > 0) {
            await updateQuestionOrderHandler(tempQuestionList);
        }
        setIsEditMode(false);
        setTempQuestionList([]);
    };

    const onNewQuestionClickHandler = () => {
        setIsAddEditQuestionModalVisible(true);
    };

    const onNewQuestionCloseHandler = () => {
        setIsAddEditQuestionModalVisible(false);
        setEditAbleQuestionObj(null);
    };

    const onSubQuestionCloseHandler = () => {
        setIsAddEditSubQuestionModalVisible(false);
        setSelectedSubQuestionForEdit(null);
    };

    const onCloseDeleteModal = () => {
        setIsDeleteFormModal(false);
        selectedQuestionForDelete.current = null;
    };

    const canAddNewQuestion = () => {
        if (formType === "single-question") {
            return questionList.length === 0;
        }
        return true;
    };

    const getButtonText = () => {
        switch (formType) {
            case "checklist":
                return "+ Add Question";
            case "single-question":
                return questionList.length === 0
                    ? "+ Add Question"
                    : "Only one question allowed";
            default:
                return "+ New Question";
        }
    };

    const renderQuestionRow = (item, index) => {
        const optionsString = sortArrayBasedOnOptionOrder(item?.fieldOptions)
            ?.map?.((obj) => obj?.fieldOption)
            ?.join(", ");

        const isQuestionExpanded = expandedQuestions.has(item.id);
        const hasOptions = item?.fieldOptions && item.fieldOptions.length > 0;

        const toggleExpand = (e) => {

            if (e.target.getAttribute("draggable") === "true" || isEditMode) return;

            if (hasOptions) {
                toggleQuestionExpand(item.id);
            }
        };

        const isDragging = draggedItem?.item.id === item.id;
        const isDropTarget = dragOverIndex === index;

        return (
            <React.Fragment key={item?.id}>
                <tr
                    draggable={isEditMode}
                    onDragStart={(e) => handleDragStart(e, item, index)}
                    onDragEnd={handleDragEnd}
                    onDragOver={(e) => handleDragOver(e, index)}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => handleDrop(e, index)}
                    onClick={toggleExpand}
                    style={{
                        cursor: isEditMode ? "grab" : hasOptions ? "pointer" : "default",
                        opacity: isDragging ? 0.5 : 1,
                        backgroundColor: isDropTarget ? "#e3f2fd" : "transparent",
                        borderTop: isDropTarget ? "2px solid #2196f3" : "none",
                        transition: "all 0.2s ease",
                    }}
                >
                    <td
                        className={`${isQuestionExpanded ? "border-0" : ""
                            } p-1 align-middle`}
                    >
                        {hasOptions && !isEditMode && (
                            <span style={{ marginRight: "5px" }}>
                                {isQuestionExpanded ? <FaChevronDown /> : <FaChevronRight />}
                            </span>
                        )}
                        {item?.fieldName ?? "-"}
                    </td>
                    <td
                        className={`${isQuestionExpanded ? "border-0" : ""
                            } p-1 align-middle`}
                    >
                        {QuestionTypes.find((obj) => obj?.value === item?.fieldType)
                            ?.label ?? "-"}
                    </td>
                    <td
                        className={`${isQuestionExpanded ? "border-0" : ""
                            } p-1 align-middle`}
                    >
                        {optionsString || "-"}
                    </td>
                    <td className={`${isQuestionExpanded ? "border-0" : "" } p-1 align-middle`} >
                        {!isEditMode && formType !== "single-question" && (
                            <button
                                className="btn btn-sm me-3"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    copyClickHandler(e, item);
                                }}
                                disabled={isDuplicating}
                                style={{
                                    opacity: isDuplicating ? 0.6 : 1,
                                    cursor: isDuplicating ? "not-allowed" : "pointer",
                                }}
                                title={isDuplicating ? "Duplicating..." : "Duplicate question"}
                            >
                                {isDuplicating ? (
                                    <i
                                        className="fa fa-spinner fa-spin"
                                        aria-hidden="true"
                                        style={{ color: "#999" }}
                                    />
                                ) : (
                                    <i
                                        className="fa fa-copy"
                                        aria-hidden="true"
                                        style={{ color: "#000" }}
                                    />
                                )}
                            </button>
                        )}

                        {!isEditMode && (
                            <button
                                className="btn btn-sm me-3"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    editClickHandler(e, item);
                                }}
                                disabled={isDuplicating}
                                style={{
                                    opacity: isDuplicating ? 0.6 : 1,
                                    cursor: isDuplicating ? "not-allowed" : "pointer",
                                }}
                            >
                                <i
                                    className="fa fa-pencil"
                                    aria-hidden="true"
                                    style={{ color: isDuplicating ? "#999" : "#000" }}
                                />
                            </button>
                        )}

                        {!isEditMode && (
                            <button
                                className="btn btn-sm me-3"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    deleteClickHandler(e, item);
                                }}
                                disabled={isDuplicating}
                                style={{
                                    opacity: isDuplicating ? 0.6 : 1,
                                    cursor: isDuplicating ? "not-allowed" : "pointer",
                                }}
                            >
                                <i
                                    className="fa fa-trash"
                                    aria-hidden="true"
                                    style={{ color: isDuplicating ? "#999" : "#000" }}
                                />
                            </button>
                        )}

                        {!isEditMode && (
                            <button className="btn btn-sm me-3" onClick={(e) => { e.stopPropagation(); previewQuestionClickHandler(e, item); }}
                                disabled={isDuplicating} style={{ opacity: isDuplicating ? 0.6 : 1, cursor: isDuplicating ? "not-allowed" : "pointer", }} >
                                <i className="fa fa-eye" aria-hidden="true" style={{ color: isDuplicating ? "#999" : "#000" }} />
                            </button>
                        )}

                        {isEditMode && (
                            <span
                                style={{
                                    color: "#6c757d",
                                    fontStyle: "normal",
                                    fontSize: "13px",
                                }}
                            >
                                Drag to reorder
                            </span>
                        )}
                    </td>
                </tr>


                {!isEditMode &&
                    isQuestionExpanded &&
                    hasOptions &&
                    item.fieldOptions.map((option) => {
                        const isOptionExpanded = expandedOptions.has(option.id);
                        const subQuestionsForOption = subQuestionList.filter(
                            (subQ) => subQ.refFieldOption === option.id
                        );

                        const hasSubQuestions = subQuestionsForOption.length > 0;

                        const handleOptionToggle = () => {
                            if (hasSubQuestions) {
                                toggleOptionExpand(option.id);
                            }
                        };

                        return (
                            <React.Fragment key={`option-${option.id}`}>
                                <tr
                                    onClick={handleOptionToggle}
                                    style={{
                                        cursor: hasSubQuestions ? "pointer" : "default",
                                        backgroundColor: "#f8f9fa",
                                    }}
                                >
                                    <td className="border-0 p-1">
                                        <span
                                            className="ml-4"
                                            style={{ display: "flex", alignItems: "center" }}
                                        >
                                            {hasSubQuestions && (
                                                <span style={{ marginRight: "5px" }}>
                                                    {isOptionExpanded ? (
                                                        <FaChevronDown />
                                                    ) : (
                                                        <FaChevronRight />
                                                    )}
                                                </span>
                                            )}

                                            {!hasSubQuestions && (
                                                <span style={{ marginRight: "5px", color: "#6c757d" }}>
                                                    •
                                                </span>
                                            )}

                                            <span className="ml-2">
                                                {option.fieldOption}

                                                {option.bgColorCode && (
                                                    <span
                                                        style={{
                                                            display: "inline-block",
                                                            width: "12px",
                                                            height: "12px",
                                                            backgroundColor: option.bgColorCode,
                                                            borderRadius: "2px",
                                                            marginLeft: "8px",
                                                            border: "1px solid #ddd",
                                                        }}
                                                        title={`Color: ${option.bgColorCode}`}
                                                    />
                                                )}

                                                {option.finishingDesc && (
                                                    <i
                                                        className="fa fa-flag-checkered"
                                                        style={{
                                                            marginLeft: "8px",
                                                            color: "#28a745",
                                                            fontSize: "12px",
                                                        }}
                                                        title="Has finishing page"
                                                    />
                                                )}

                                                {hasSubQuestions && (
                                                    <i
                                                        className="fa fa-sitemap"
                                                        style={{
                                                            marginLeft: "8px",
                                                            color: "#28a745",
                                                            fontSize: "12px",
                                                        }}
                                                        title="Has sub-questions"
                                                    />
                                                )}
                                            </span>
                                        </span>
                                    </td>
                                    <td className="border-0"></td>
                                    <td className="border-0"></td>
                                    <td className="border-0"></td>
                                </tr>


                                {isOptionExpanded &&
                                    hasSubQuestions &&
                                    subQuestionsForOption.map((subQ) => (
                                        <tr
                                            key={`subq-${subQ.id}`}
                                            style={{ backgroundColor: "#e9ecef" }}
                                        >
                                            <td className="p-1 align-middle">
                                                <span
                                                    className="ml-5"
                                                    style={{ display: "flex", alignItems: "center" }}
                                                >
                                                    <MdSubdirectoryArrowRight className="me-2" />
                                                    {subQ?.fieldName ?? "-"}
                                                </span>
                                            </td>
                                            <td className="p-1 align-middle">
                                                {QuestionTypes.find(
                                                    (obj) => obj?.value === subQ?.fieldType
                                                )?.label ?? "-"}
                                            </td>
                                            <td className="p-1 align-middle">
                                                {sortArrayBasedOnOptionOrder(subQ?.fieldOptions)
                                                    ?.map?.((obj) => obj?.fieldOption)
                                                    ?.join(" , ")}
                                            </td>
                                            <td className="p-1 align-middle">
                                                <button
                                                    className="btn btn-sm me-3"
                                                    onClick={(e) => editClickHandler(e, subQ)}
                                                >
                                                    <i
                                                        className="fa fa-pencil"
                                                        aria-hidden="true"
                                                        style={{ color: "#000" }}
                                                    />
                                                </button>
                                                <button
                                                    className="btn btn-sm me-3"
                                                    onClick={(e) => deleteClickHandler(e, subQ)}
                                                >
                                                    <i
                                                        className="fa fa-trash"
                                                        aria-hidden="true"
                                                        style={{ color: "#000", cursor: "pointer" }}
                                                    />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                            </React.Fragment>
                        );
                    })}
            </React.Fragment>
        );
    };

    if (isLoading || parentLoading) {
        return (
            <div style={{ textAlign: "center", padding: "40px" }}>
                <Loader />
            </div>
        );
    }

    return (
        <div className="table-wrap padding_infor_info">
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "15px",
                    marginBottom: "20px",
                }}
            >
                <button
                    className="btn btn-outline-info btn-lg px-4 fw-bold"
                    style={{
                        opacity: canAddNewQuestion() && !isEditMode ? 1 : 0.5,
                        cursor:
                            canAddNewQuestion() && !isEditMode ? "pointer" : "not-allowed",
                    }}
                    onClick={onNewQuestionClickHandler}
                    disabled={!canAddNewQuestion() || isEditMode}
                >
                    {getButtonText()}
                </button>

                {questionList.length > 1 && !isEditMode && (
                    <button
                        className="btn btn-outline-secondary btn-lg px-4 fw-bold"
                        onClick={handleEditOrderClick}
                        style={{
                            opacity: isDuplicating ? 0.5 : 1,
                            cursor: isDuplicating ? "not-allowed" : "pointer",
                        }}
                        disabled={isDuplicating}
                    >
                        <i
                            className="fa fa-sort"
                            style={{
                                marginRight: "8px",
                                color: "currentColor",
                            }}
                        ></i>
                        Edit Order
                    </button>
                )}

                {/* {isEditMode && (
                    <div style={{ 
                        color: "#6c757d", 
                        fontStyle: "italic",
                        fontSize: "14px",
                        marginLeft: "auto"
                    }}>
                        Drag questions to reorder them, then click "Save Order"
                    </div>
                )} */}
            </div>

            <table className="table table-hover">
                <thead>
                    <tr>
                        <th>Question Text</th>
                        <th width="15%">Type</th>
                        <th width="20%">Options</th>
                        <th width="20%">Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {isLoading ? (
                        <tr>
                            <td colSpan="4" className="text-center">
                                <Loader />
                            </td>
                        </tr>
                    ) : (isEditMode ? tempQuestionList : questionList)?.length > 0 ? (
                        (isEditMode ? tempQuestionList : questionList).map((item, index) =>
                            renderQuestionRow(item, index)
                        )
                    ) : (

                        <NoDataFound />

                    )}
                </tbody>
            </table>

            {isEditMode && (
                <div style={{ display: "flex", gap: "10px" }}>
                    <button className="btn cur-p btn-primary" onClick={handleSaveOrder}>
                        Save
                    </button>

                    <button
                        className="btn cur-p btn-secondary"
                        onClick={handleCancelEdit}
                    >
                        Cancel
                    </button>
                </div>
            )}

            {/* Modals */}
            <AddEditQuestionModal
                editAbleQuestionObj={editAbleQuestionObj}
                formId={formDetailsObj?.id}
                formType={formType}
                questionList={questionList}
                subQuestionList={subQuestionList}
                isVisible={isAddEditQuestionModalVisible}
                setEditAbleQuestionObj={setEditAbleQuestionObj}
                refetchQuestionsList={fetchAllQuestionListHandler}
                onClose={onNewQuestionCloseHandler}
                customEmailText={customEmailText}
            />

            <PreviewQuestionModal isVisible={isPreviewQuestionModalVisible} onClose={onPreviewQuestionCloseHandler}
                Question={PreviewAbleQuestionObj} formType={formType} />

            <AddEditSubQuestionModal
                editAbleQuestionObj={
                    selectedSubQuestionForEdit
                        ? questionList.find((q) =>
                            q.fieldOptions?.some(
                                (opt) => opt.id === selectedSubQuestionForEdit.refFieldOption
                            )
                        )
                        : null
                }
                isVisible={isAddEditSubQuestionModalVisible}
                SelectedOptionId={selectedSubQuestionForEdit?.refFieldOption}
                onClose={onSubQuestionCloseHandler}
                refetchQuestionsList={fetchAllQuestionListHandler}
                formType={formType}
                subQuestionList={subQuestionList}
                currentEditingSubQuestion={selectedSubQuestionForEdit}
                standalone={true}
                customEmailText={customEmailText}
            />

            <DeleteModal
                visible={isDeleteFormModal}
                onClose={onCloseDeleteModal}
                onCancel={onCloseDeleteModal}
                onDelete={deletedFromHandler}
                title={"Are you sure you want to delete this question?"}
            />
        </div>
    );
};

export default memo(QuestionsManagement);
